/* eslint-disable @next/next/no-img-element */
import React, { useMemo } from 'react';
import classNames from 'classnames';
import CalcRadish from '$/components/CalcRadish';
import IconFont from '$/components/IconFont';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { Popover } from 'antd';
import preQrCode from '@/assets/pre-qrcode.png';
import betaQrCode from '@/assets/beta-qrcode.png';
import relaseQrCode from '@/assets/release-qrcode.png';
import { getEnv, navigate } from '@/common/agents';
import { getHomeRouteByVersion } from '@/common/homeVersionCookie';
import { pageMap } from '@/common/pageType';
import { useCommonStore } from '@/store/useCommonStore';
import ProductOperation from '../ProductOperation';
import LanguageSwitch from '../LanguageSwitch';
import styles from './index.module.scss';
import UserCard from '../UserCard';
import TeamPackageBadge from '../TeamPackageBadge';
import DiscordBtn from './DiscordBtn';

/** IP 列表和创建页沿用首页顶部用户区，创建页补充当前会员等级标签。 */
export default function Header() {
  const isOversea = useOfficialStore((state) => state.isOversea());
  const languageStore = useLanguageStore((state) => state.languageStore);
  const headerConfig = useCommonStore((state) => state.headerConfig);
  const currentPathname = useCommonStore((state) => state.currentPathname);
  const path = (currentPathname || '').split('?')[0].replace(/\/$/, '') || '';
  const isHome = path === '/--roboneo/home' || path === '/home';
  const isHomeOld = path === '/--roboneo/home_old' || path === '/home_old';
  const isTeamInvite = path === '/--roboneo/team_invite' || path === '/team_invite';
  const isMyIpPage = pageMap.myips.regExp.test(path);
  const isMyIpCreationPage = pageMap.myIpCreation.regExp.test(path);
  // 角色创建页也使用萝卜和头像组合，避免落入不显示会员名称的旧版头部布局。
  const showUnifiedUserInfo = isHome || isTeamInvite || isMyIpPage || isMyIpCreationPage;
  const switchToNewText = languageStore.smartcatTranslationText((v) => v.switch_to_new_version);
  const switchToOldText = languageStore.smartcatTranslationText((v) => v.switch_to_old_version);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const hasValidMembership = useOfficialStore((state) => !!state.userInfo.subscribe?.isValid);

  /**
   * 从新版首页切换到旧版首页入口。
   */
  const goHomeOld = () => {
    window.mtstat?.track('roboneo_new_or_old_veision', {
      current_version: 'new',
    });
    navigate({ mode: 'router', pathname: getHomeRouteByVersion('old') });
  };

  /**
   * 从旧版首页切换回新版首页入口。
   */
  const goHomeNew = () => {
    window.mtstat?.track('roboneo_new_or_old_veision', {
      current_version: 'old',
    });
    navigate({ mode: 'router', pathname: getHomeRouteByVersion('new') });
  };

  const openQrCode = (open: boolean) => {
    if (open) {
      window.mtstat?.track('download_mobile_hover', {});
    }
  };

  const downloadContent = () => {
    const QR_CODE = {
      development: preQrCode,
      dev: preQrCode,
      pre: preQrCode,
      beta: betaQrCode,
      release: relaseQrCode,
    }[getEnv().ENV];

    return (
      <div className={styles.downloadPopverContent}>
        <div className={styles.qrcodeBox}>
          <img
            className={styles.qrcode}
            src={QR_CODE.src}
            alt=""
            crossOrigin="anonymous"
          />
        </div>
      </div>
    );
  };

  const wechatQrcodeContent = useMemo(() => {
    return (
      <div className={styles.wechatQrcodeContent}>
        <img src="https://titan-img.meitudata.com/roboneo/image/home/wechat_qrcode.png" alt="" crossOrigin="anonymous" />
      </div>
    );
  }, []);

  return (
    <div
      className={classNames(styles.Header, { [styles.withHomeSeedanceGuide]: isHome || isHomeOld })}
      style={{
        backgroundColor: headerConfig.showHeaderBackgroundColor ? 'var(--background-canvas)' : 'transparent',
      }}
    >

      <div className={styles.gradientBlur} style={{ visibility: headerConfig.showBlurBackground ? 'visible' : 'hidden' }}>
        <div />
        <div />
        <div />
        <div />
        <div />
        <div />
      </div>
      <div className={classNames(styles.headerContent, { [styles.homeHeaderContent]: isHome || isMyIpPage || isMyIpCreationPage })}>
        {isHomeOld && (
          <button className={styles.versionSwitchButton} type="button" onClick={goHomeNew} aria-label={switchToNewText}>
            <IconFont name="arrowLeftAndRightCircleBold" fontSize={16} />
            <span>{switchToNewText}</span>
          </button>
        )}
        {isHome && (
          <button className={styles.versionSwitchButton} type="button" onClick={goHomeOld} aria-label={switchToOldText}>
            <IconFont name="arrowLeftAndRightCircleBold" fontSize={16} />
            <span>{switchToOldText}</span>
          </button>
        )}
        {/* 语言切换 */}
        <LanguageSwitch />

        <div className={styles['product-card-box']}>
          <div className={styles['product-item']}>
            <ProductOperation />
          </div>
          {isOversea
            ? <DiscordBtn />
            : (
              <Popover
                placement="bottom"
                trigger={['hover']}
                content={wechatQrcodeContent}
                overlayClassName="resetPopoverAndArrow"
              >
                <div className={styles['product-item']}>
                  <div
                    style={{
                      width: '40px',
                      display: 'flex',
                      alignContent: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconFont
                      className={styles['product-item-icon']}
                      fontSize={16}
                      name="wechat"
                    />
                  </div>
                </div>
              </Popover>
            )}

          <div className={styles['product-item']}>
            <Popover
              placement="bottom"
              trigger={['hover']}
              overlayClassName="resetPopoverAndArrow"
              content={downloadContent()}
              onOpenChange={openQrCode}
            >
              <div className={styles.downloadBox} style={{ height: '100%' }}>
                <IconFont
                  className={styles['product-item-icon']}
                  fontSize={16}
                  name="mobilePhoneV2Bold"
                />
              </div>
            </Popover>
          </div>

        </div>

        {/* 首页、IP 页面与团队邀请页共用独立萝卜按钮和个人中心头像。 */}
        {showUnifiedUserInfo && isUserLoggedIn ? (
          <div className={styles.homeUserActions}>
            <CalcRadish variant="homeHeader" />
            {isMyIpCreationPage && hasValidMembership && <TeamPackageBadge />}
            <UserCard variant="homeHeader" />
          </div>
        ) : (
          <>
            {/* 用户卡片 */}
            <UserCard />
            {/* 萝卜余额 */}
            <CalcRadish />
          </>
        )}
      </div>
    </div>
  );
}
